/**
 * Part 11.2 - a page that shows a run shows it for the active site, from that
 * site's own completed runs. A site with none says so, instead of showing
 * another site's flood under this site's name.
 */

import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import { useAppStore } from '../store/useAppStore'

interface SitesPayload {
  run_site: string
  sites: { id: string; display_name: string; dem_available: boolean }[]
}

export function RunScoped({ page, children }: { page: string; children: ReactNode }) {
  const active = useAppStore((s) => s.activeSiteId)
  const setActive = useAppStore((s) => s.setActiveSiteId)
  const sites = useQuery<SitesPayload>({ queryKey: ['sites'], queryFn: () => api.raw('/sites') })
  const runs = useQuery({ queryKey: ['runs'], queryFn: api.runs })
  if (!sites.data || !runs.data) return <div className="skeleton m-6 h-24" />
  if (runs.data.some((r) => r.status === 'done' && r.site_id === active)) return <>{children}</>
  const site = sites.data.sites.find((s) => s.id === active)
  const owner = sites.data.sites.find((s) => s.id === sites.data.run_site)
  return (
    <div className="p-8" data-testid="run-scoped-notice">
      <div className="max-w-2xl rounded border border-amber-700 bg-amber-950/30 p-4 text-sm leading-relaxed text-amber-100">
        <div className="font-semibold text-amber-300">{page}: nothing to show for {site?.display_name ?? active}</div>
        <p className="mt-1">
          Every 2-D run on this platform is of {owner?.display_name ?? sites.data.run_site}.{' '}
          {site && !site.dem_available
            ? 'This site has no DEM, so no 2-D run of it can exist; its site dashboard says what is available instead.'
            : 'No 2-D run has been made for this site yet.'}
        </p>
        <div className="mt-3 flex gap-3 text-14">
          <Link to={`/site/${active}`} className="underline">
            Open the site dashboard
          </Link>
          <button type="button" className="underline" onClick={() => setActive(sites.data.run_site)}>
            Switch to {owner?.display_name ?? sites.data.run_site}
          </button>
        </div>
      </div>
    </div>
  )
}
